/**
 * usePodLapScheduler — runtime scheduler for Listening Pods (Layer 2).
 *
 * Sibling to MetaCommentaryService. Replaces the previous script-baked
 * approach where pod laps were emitted into rounds at every main-round ≥
 * activation, with stage progression keyed off `main_round - activation + 1`.
 *
 * The old model coupled pod state to main-flow position. Belt-skipping
 * (round 60 → 120) caused a 60-sentence avalanche. Going back to earlier
 * rounds re-fired pod content. Skipping a single pod meant losing it.
 *
 * New model — Listening Pods are an independent track with their own
 * ratchet counter (`course_enrollments.completed_pod_rounds`):
 *
 *   • The counter's unit is SENTENCES covered (its historical value — one
 *     legacy lap introduced one sentence — so live learners migrate in
 *     place, no reset, no write-time transform)
 *   • Intake is by COHORT — one EXCHANGE (a speaker turn plus its reply),
 *     scenes as walls (Tom 2026-07-24): each lap debuts one cohort;
 *     pod-round in stage maths = the cohort-round derived via
 *     podCohortRoundFor(cohorts, counter)
 *   • Each completed lap → counter snaps to the debuted cohort's end
 *     boundary (or +1 once all cohorts are in, so aging never freezes)
 *   • Skipped lap → counter unchanged → same content next session
 *   • Belt-skip → no avalanche; pods just keep advancing one per played lap
 *   • Going back to earlier main rounds → pods continue forward
 *   • Course reset → counter back to 0 (and the work-debt counter to 0)
 *   • TOP OF THE LADDER COMPLETES (Tom 2026-09-06): a cohort that has served
 *     every rung, top one included, is dropped from the lap for good — it is no
 *     longer in the sequence. The pods stay always-available in the Pods tab of
 *     Listening mode. When every cohort has completed, the pod track stops
 *     claiming boundaries at all (allCohortsCompleted).
 *
 * CADENCE IS A DEBT, NOT A SCHEDULE (2026-09-05, replacing the 2026-05-03
 * `pod_activation_round` pin + position-modulus rule). `rounds_since_pod` on
 * course_enrollments counts EVERY completed round — replays, easy mode,
 * revival-tail rounds — and a lap fires at the first clean boundary where the
 * debt reaches `roundInterval` (5). It resets to 0 only on lap COMPLETION;
 * skip, failure, offline-incomplete and composed-nothing all leave the debt
 * standing. Position never enters the rule, so a learner whose position crawls
 * (Beuno: 33 rounds of work, position 10→14, no pod in a month) still gets the
 * listening they earned.
 *
 * Guests (no enrollment row): use an in-memory counter that resets each
 * session. Friendlier than skipping pods entirely; matches the model where
 * guest progress is ephemeral anyway.
 */

import { ref, shallowRef, type Ref } from 'vue'
import type { SupabaseClient } from '@supabase/supabase-js'
import {
  type AtomMapEntry,
  ROLE_SPEED,
  isTargetRole,
  buildMainStage,
  buildPodCohorts,
  podCohortOrdinalForIndex,
  podCohortRoundFor,
  podRatchetAfterLap,
  type PodCohort,
  type PodPlayRole,
  type PodPlay,
  type PodSentenceRow,
} from '@ssi/core/pods'
import { PodStateStore } from '@ssi/core'
import { splitRowUnits } from './podSentenceSplit'
import { baseSlate, continuationsByBranch, type SlateRow, type PodContinuation } from './podSlate'
import { getCachedListeningMeta, retryListeningRead } from './listeningMetaCache'
import { resolveServedPod } from './servedPod'
import { capConsecutiveRepeats } from '../playback/capConsecutiveRepeats'
import {
  type ListeningSlot,
  DEFAULT_FAST_BELT_CEILINGS,
  DEFAULT_FAST_LISTENING_RAMP,
  DEFAULT_LISTENING_PATTERN,
  LISTENING_SPEED_CEILING,
  beltCeilingForSeed,
  resolveListeningSpeed,
} from '../playback/listeningExposureRamp'
import type { ListeningPlayPolicy } from './useAlgorithmConfig'
import type { TargetSpeedConfig } from '../providers/toSimpleRounds'
import { collectAudioIds, getRevisedAudioRefs, stampRowAudioRefs, bareAudioId } from '../providers/revisedAudioRefs'
import { apiUrl } from '@/platform/apiBase'

/**
 * A-64 (Tom, 2026-08-06): "no mode should ever repeat the same prompt more than
 * twice consecutively". A pod play's prompt is the clip it plays, so identity is
 * the audio id — 'ps' and 'ps2x' are the same clip at different speeds and count
 * as the same prompt, which is exactly the run the law is aimed at (stage 4's
 * ['ps','trans','ps2x','ps2x'] is legal; stage 7's ['ps2x','ps2x'] repeated on
 * the very next lap is not).
 */
const podPlayIdentity = (play: PodPlay): string => play.audioId

// Re-export the moved symbols so existing importers (LearningPlayer,
// ListeningOverlay, tests) keep their import paths.
export {
  ROLE_SPEED,
  isTargetRole,
  type PodPlayRole,
  type PodPlay,
  type PodSentenceRow,
} from '@ssi/core/pods'

// ============================================================================
// Pod stage logic — mirrors what was in generateLearningScript.ts
// ============================================================================

// PodPlayRole + ROLE_SPEED now live in ./podStageComposition (re-exported above).

/**
 * Default stage playlists — mirrored to the `pods` row in algorithm_config.
 * PS = pod sentence at 1.0×, PS×2 at 2.0×, trans = known-language translation.
 *
 * The number of stages is *not* hardcoded in the runtime — podStageFor reads
 * `totalStages` from the actual playlist. Admins can add / remove stages
 * from the listening admin page without code changes; the highest-numbered
 * stage is the LAST rung of the ladder — served for its own dwell, after which
 * the cohort completes and leaves the sequence (Tom, 2026-09-06; see
 * podCohortHasCompleted). It used to be an eternal hold.
 *
 * Stage 1 is intentionally all 1.0× — no 2× until stage 2 — so the learner
 * gets a clean target / known / target / target intro. Aran's 2026-05-07
 * insertion (new stage 2) bridges the jump from "no 2×" to "all 2×".
 */
/**
 * THIS MAP IS A FALLBACK, NOT THE LADDER LEARNERS HEAR — read this before
 * believing anything below.
 *
 * Corrected 2026-09-06 (#704) against the live DB. The previous note here said
 * the stage ladder was dead on the learner path because
 * `listeningUseStagePlaylist` was unset. IT IS SET: the live `listening` row in
 * algorithm_config carries `"listeningUseStagePlaylist": true`, description
 * "stage playlist ON 2026-08-24". So nextLap() DOES take the stage-playlist
 * branch for every real learner, and the fade ladder is what they hear.
 *
 * What is still true is that this code map is overridden: the LIVE `pods` row
 * in algorithm_config supplies the actual ladder. Since job #318 (2026-09-20)
 * that row is NINE stages, one lap each on 1..8 and the terminal stage 9
 * ['ps2x'] taking the remaining two of retireAfterVisits' ten, after which the
 * sentence retires; job #335 the same day replaced what each of those nine
 * rungs PLAYS with the ladder Tom ruled by ear — each rung removing exactly
 * one thing, speed only rising, length only falling. It is mirrored verbatim
 * in `DEFAULT_PODS.stagePlaylist`
 * (useAlgorithmConfig), which is the copy to read if you want the live shape;
 * the map below is an older, close-but-not-identical fallback. The per-stage comments describe the retired code ladder, kept
 * as its record.
 */
export const DEFAULT_STAGE_PLAYLIST: Record<number, PodPlayRole[]> = {
  // Stage 1 — "Phase 0" (Tom 2026-06-10): the sentence's introduction.
  // Raw target, translation, target again. Lasts ONE pod-round
  // (DEFAULT_STAGE_DURATIONS), then retires for good. (This slot used to be an
  // explainer clip; those were removed 2026-10-04 and the slot was already
  // playing the translation for every sentence.)
  1: ['ps', 'trans', 'ps'],
  // Stage 2 — "Phase 1": explainer retired, regular translation pattern,
  // still all 1.0×. Lasts 3 pod-rounds. Aran's 2026-05-07 bridge stage
  // (no 2× → all 2×) follows from stage 3.
  2: ['ps', 'trans', 'ps'],
  3: ['ps', 'trans', 'ps', 'ps2x'],
  4: ['ps', 'trans', 'ps2x', 'ps2x'],
  5: ['ps', 'trans', 'ps2x'],
  6: ['ps2x', 'trans', 'ps2x'],
  7: ['ps', 'ps2x'],
  8: ['ps2x', 'ps2x'],
  9: ['ps2x'],
}

/**
 * Default pod-rounds spent in each stage before promoting. The final stage
 * used to be eternal; since Tom's 2026-09-06 ruling it too lasts its dwell and
 * then the cohort completes and disappears. Aran asked for 5 (was 3) — gives
 * the learner more reps at each pattern before the speed/structure changes.
 */
export const DEFAULT_STAGE_DURATION = 5

/**
 * Per-stage duration overrides (pod-rounds). Stages not listed fall back
 * to the uniform stageDuration — so stage 1 must stay LISTED even at 1:
 * deleting the key would silently give it DEFAULT_STAGE_DURATION (5).
 *
 * Stage 1 = 1 round (Tom, 2026-08-10). It used to be 2 so the explainer was
 * heard exactly twice; Tom cancelled that: "we don't want it at all — the
 * visualisation covers the need to explain". The karaoke-scroll does the
 * explaining job now, so the introduction gets one bite, like every other
 * non-repeating stage. Phase 1 (stage 2) = 3 rounds, unchanged (Tom
 * 2026-06-10).
 */
export const DEFAULT_STAGE_DURATIONS: Record<number, number> = {
  1: 1, 2: 1, 3: 1, 4: 1, 5: 1, 6: 1, 7: 1, 8: 1,
}

/**
 * TEN VISITS PER SENTENCE (Tom, 2026-09-19): "each stage should repeat once —
 * for simplicity — apart from Stage 9, which should retire after maybe 2
 * repeats, else we're just building up the listening until it's too long. This
 * will mean each 'sentence' goes through a total of 10 'visits'."
 *
 * So: stages 1..8 one lap each (DEFAULT_STAGE_DURATIONS above — every one of
 * them LISTED, because an unlisted stage silently falls back to the uniform
 * DEFAULT_STAGE_DURATION of 5), stage 9 twice, then the cohort completes and
 * leaves the sequence. That is 8 + 2 = 10.
 *
 * The terminal rung's dwell is NOT listed, deliberately: this number is the
 * ladder's whole length and the top rung absorbs the remainder, so a ladder
 * with a different number of rungs still retires at ten. One knob, movable
 * from the Listening Lab without a deploy.
 */
export const DEFAULT_RETIRE_AFTER_VISITS = 10

/**
 * Map an alive-count to a stage. Transitional stages 1..(totalStages-1)
 * each last `stageDuration` pod-rounds; the highest-numbered stage is returned
 * for every alive-count past them (iter null). Since Tom's 2026-09-06 ruling
 * that is NOT an eternal hold — the lap composer graduates the cohort once it
 * has served the top rung's dwell too (podCohortHasCompleted); this function is
 * only the stage MAP and is unchanged. `totalStages` defaults to the length of DEFAULT_STAGE_PLAYLIST
 * so unit tests calling podStageFor() without args still see consistent
 * behaviour; callers in the runtime pass the live playlist's key count.
 */
export function podStageFor(
  entryPodRound: number,
  currentPodRound: number,
  stageDuration: number = DEFAULT_STAGE_DURATION,
  totalStages: number = Object.keys(DEFAULT_STAGE_PLAYLIST).length,
  /** Per-stage duration overrides (e.g. {1: 2, 2: 3}); unlisted stages use
   *  the uniform stageDuration. Omit for legacy uniform behaviour. */
  stageDurations?: Record<string | number, number>,
): { stage: number; iter: number | null } | null {
  const alive = currentPodRound - entryPodRound + 1
  if (alive < 1) return null
  let cum = 0
  for (let stage = 1; stage < totalStages; stage++) {
    const d = stageDurations?.[stage] ?? stageDurations?.[String(stage)] ?? stageDuration
    if (alive <= cum + d) {
      return { stage, iter: alive - cum }
    }
    cum += d
  }
  return { stage: totalStages, iter: null }
}

/**
 * THE LADDER COMPLETES (Tom, 2026-09-06): "top of the ladder it then goes -
 * completes, disappears, is no longer in the sequence - the PODS are all
 * always available in the listening mode section".
 *
 * So the highest-numbered stage is NO LONGER an eternal hold. A cohort climbs
 * every rung including the top one, serves the top rung for its own dwell, and
 * then GRADUATES: it is dropped from the lap composer and never emitted into
 * the pod sequence again. It does not sit at the top in permanent target-only
 * immersion (rejected) and it does not stop a rung short and keep cycling
 * (rejected). The sentence keeps living in the Pods tab of Listening mode,
 * where the learner can always open its scene and play it — nothing is
 * deleted from the learner's world, only from the automatic sequence.
 *
 * WHY THE TOP RUNG IS SERVED RATHER THAN SKIPPED. "Top of the ladder it THEN
 * goes" — it arrives, does the top rung, then leaves. Graduating on ARRIVAL at
 * the terminal stage would make that rung dead content: Tom's own 2026-08-24
 * fade ladder authors stage 8 as the bare t@2× close of the fade, and skipping
 * it would silently delete the rung he wrote. So the ladder's total length =
 * every stage's dwell, terminal stage included, and graduation is the round
 * AFTER that.
 *
 * Returns the total pod-rounds a cohort spends on the ladder before it
 * completes. A single-stage config has no ladder to climb, so it is treated as
 * never-graduating (Infinity) rather than graduating everything at once.
 */
export function podLadderTotalRounds(
  stageDuration: number = DEFAULT_STAGE_DURATION,
  totalStages: number = Object.keys(DEFAULT_STAGE_PLAYLIST).length,
  stageDurations?: Record<string | number, number>,
  /** TEN VISITS, AND THE NUMBER IS ONE KNOB (Tom, 2026-09-19). When set this
   *  IS the ladder length in visits, whatever the per-stage durations add up
   *  to — so the terminal rung absorbs whatever is left over and a sentence
   *  can never outstay it. Unset ⇒ derive from the durations, as before. */
  retireAfterVisits?: number,
): number {
  if (typeof retireAfterVisits === 'number' && Number.isFinite(retireAfterVisits) && retireAfterVisits >= 1) {
    return Math.floor(retireAfterVisits)
  }
  if (!Number.isFinite(totalStages) || totalStages < 2) return Infinity
  let total = 0
  for (let stage = 1; stage <= totalStages; stage++) {
    total += stageDurations?.[stage] ?? stageDurations?.[String(stage)] ?? stageDuration
  }
  return total
}

/**
 * Has this cohort finished the ladder and completed? `alive` is its exposure
 * count (the number of laps it has been in play — the same value podStageFor
 * takes). True ⇒ drop it from the lap. See podLadderTotalRounds for the ruling.
 */
export function podCohortHasCompleted(
  alive: number,
  stageDuration: number = DEFAULT_STAGE_DURATION,
  totalStages: number = Object.keys(DEFAULT_STAGE_PLAYLIST).length,
  stageDurations?: Record<string | number, number>,
  retireAfterVisits?: number,
): boolean {
  return alive > podLadderTotalRounds(stageDuration, totalStages, stageDurations, retireAfterVisits)
}

// The activation pin (DEFAULT_POD_ACTIVATION) and its 2026-05-20 stale-value
// cap (POD_ACTIVATION_CAP) are DELETED: both existed only to tame a
// position-modulus cadence. A work-debt counter starts from measured work,
// needs no pin, and can produce no avalanche (it only ever has to reach 5).

// isTargetRole + PodSentenceRow now live in ./podStageComposition (imported above).

// ============================================================================
// Types
// ============================================================================

/**
 * Raw listening_pod_sentences row as fetched — a whole speaker TURN. Flattened
 * into per-SENTENCE PodSentenceRow units by flattenPodRows when the turn was
 * silence-split (sentence_audio_ids present).
 */
interface RawPodRow {
  id: string
  global_order: number
  scene_number?: number | null
  speaker: string | null
  target_text: string
  known_text: string
  target_audio_id: string | null
  known_audio_id: string | null
  glue_to_next: boolean
  atom_map?: AtomMapEntry[] | null
  sentence_audio_ids?: string[] | null
  sentence_known_audio_ids?: string[] | null
}

/** A flattened per-sentence pod row. Plain alias — kept as its own name so
 *  call sites (podSentences ref, LearningPlayer's turn display) don't couple
 *  directly to the core type name. */
export type SchedulerPodRow = PodSentenceRow

/** Strip everything but letters/numbers/combining-marks for a script-agnostic
 *  structural comparison (NOT a linguistic judgment — just whitespace/
 *  punctuation normalisation so pre-decided atom surfaces can be matched to
 *  pre-decided sentence text). \p{M} (combining marks) MUST be kept — dropping
 *  it would collapse e.g. Devanagari नमस्ते vs नमस्तें to the same string and
 *  wrongly accept a misaligned partition. Letters/numbers/marks cover every
 *  script; only separators + punctuation are stripped. */
const alnumOnly = (s: string): string => (s || '').toLowerCase().replace(/[^\p{L}\p{N}\p{M}]/gu, '')

/**
 * Partition a turn's FLAT atom_map across its sentences, so each split sentence
 * gets only its OWN atoms for the per-sentence visual breakdown (LegoAssembly
 * tiles). The atom_map is ordered and its atom surfaces tile the turn's target
 * text in order, so we walk the atoms accumulating their surfaces and close a
 * group when the accumulation exactly covers the next sentence. Returns one
 * atom group per sentence, or NULL if the atoms don't cleanly align (then the
 * caller drops the tiled breakdown for that turn's splits — never a WRONG
 * one). 'note' entries (no spoken surface) ride along in the current group;
 * only atom/passthrough surfaces are matched.
 */
export function partitionAtomMap(
  atomMap: AtomMapEntry[] | null | undefined,
  sentenceTexts: string[],
): AtomMapEntry[][] | null {
  if (!Array.isArray(atomMap) || atomMap.length === 0) return null
  if (sentenceTexts.length < 2) return null
  const targets = sentenceTexts.map(alnumOnly)
  if (targets.some((t) => !t)) return null
  const groups: AtomMapEntry[][] = sentenceTexts.map(() => [])
  let si = 0
  let acc = ''
  for (const entry of atomMap) {
    if (si >= groups.length) return null // more atoms than sentences to hold them
    groups[si].push(entry)
    if (entry.kind === 'atom' || entry.kind === 'passthrough') {
      const surf = alnumOnly(entry.target_surface || '')
      if (surf) {
        acc += surf
        if (acc === targets[si]) { si++; acc = '' }
        else if (!targets[si].startsWith(acc)) return null // misalignment
      }
    }
  }
  // Every sentence must be consumed exactly, every group non-empty.
  if (si !== groups.length || acc !== '') return null
  if (groups.some((g) => g.length === 0)) return null
  return groups
}

/**
 * Flatten raw turn-rows into per-SENTENCE PodSentenceRows. A row with a
 * silence-split (sentence_audio_ids ≥ 2) becomes one row per sentence, each
 * carrying its OWN target/known clip + text + partitioned atom_map; a row
 * without a split passes through unchanged. glue_to_next is recomputed
 * speaker-aware: a sentence glues to the next when they share a speaker (a
 * paragraph runs tight; a speaker change breathes). Split siblings of one turn
 * always glue; speakerless pods fall back to the row's original glue_to_next.
 */
export function flattenPodRows(rawRows: RawPodRow[], textById?: Map<string, string>): SchedulerPodRow[] {
  type Expanded = SchedulerPodRow & { _turnId: string; _origGlue: boolean }
  const expanded: Expanded[] = []
  // Sibling detection keys off the SOURCE-ROW index (unique per turn), not
  // row.id — robust to a missing/duplicate id; sub-sentences of one turn share it.
  rawRows.forEach((row, rowIdx) => {
    const turnId = String(rowIdx)
    const units = splitRowUnits(row, textById)

    if (units.length === 1) {
      expanded.push({
        // Shared two-doors counter key — same convention the overlay uses.
        sentence_id: row.id || null,
        global_order: row.global_order,
        scene_number: row.scene_number ?? null,
        target_text: row.target_text,
        known_text: row.known_text,
        target_audio_id: row.target_audio_id ?? null,
        known_audio_id: row.known_audio_id ?? null,
        glue_to_next: !!row.glue_to_next,
        atom_map: row.atom_map ?? null,
        speaker: row.speaker ?? null,
        _turnId: turnId,
        _origGlue: !!row.glue_to_next,
      })
    } else {
      const atomGroups = partitionAtomMap(row.atom_map, units.map((u) => u.targetText))
      units.forEach((u, k) => {
        expanded.push({
          sentence_id: row.id ? `${row.id}:s${k}` : null,
          global_order: row.global_order + k * 0.001,
          scene_number: row.scene_number ?? null,
          target_text: u.targetText,
          known_text: u.knownText,
          target_audio_id: u.targetAudioId,
          known_audio_id: u.knownAudioId,
          glue_to_next: false, // set in the speaker-aware pass below
          atom_map: atomGroups ? atomGroups[k] : null,
          speaker: row.speaker ?? null,
          _turnId: turnId,
          _origGlue: !!row.glue_to_next,
        })
      })
    }
  })
  for (let k = 0; k < expanded.length; k++) {
    const cur = expanded[k]
    const next = expanded[k + 1]
    if (!next) { cur.glue_to_next = false; continue }
    if (next._turnId === cur._turnId) { cur.glue_to_next = true; continue } // sibling sentence of one turn
    const a = (cur.speaker || '').trim().toLowerCase()
    const b = (next.speaker || '').trim().toLowerCase()
    cur.glue_to_next = a && b ? a === b : cur._origGlue
  }
  return expanded.map(({ _turnId: _t, _origGlue: _g, ...row }) => row)
}

export interface BookendAudio {
  id: string
  text: string
  duration_ms?: number
}

// PodPlay now lives in ./podStageComposition (re-exported above).

export interface PodLap {
  /** Cohort-round this lap represents (podCohortRoundFor of the ratchet):
   *  round N debuts cohort N and is the stage-aging clock. */
  podRound: number
  /** Bookend to play before the lap. Null if not generated for this course. */
  intro: BookendAudio | null
  /** Plays in lap order. */
  plays: PodPlay[]
  /** Bookend to play after the lap. Null if not generated for this course. */
  outro: BookendAudio | null
}

export interface UsePodLapSchedulerOptions {
  supabase: Ref<SupabaseClient | null> | SupabaseClient | null
  /** Reactive course code — scheduler re-loads when this changes. */
  courseCode: Ref<string> | string
  /** Reactive learner ID. Guests (id starts with `guest-`) use in-memory counter. */
  learnerId: Ref<string | null | undefined> | string | null | undefined
  /** Stage playlist override — keyed by stage number ('1'..'7'). When omitted,
   *  uses DEFAULT_STAGE_PLAYLIST. Reactive so admin tweaks via algorithm_config
   *  flow through on next lap. */
  stagePlaylist?: Ref<Record<string, PodPlayRole[]>> | Record<string, PodPlayRole[]>
  /** Pod-rounds per stage 1-6. Defaults to DEFAULT_STAGE_DURATION. */
  stageDuration?: Ref<number> | number
  /** Per-stage duration overrides keyed by stage number (e.g. {'1': 2, '2': 3}
   *  — Phase 0 plays twice, Phase 1 three times). Unlisted stages use
   *  stageDuration. Omitted → uniform legacy behaviour. */
  stageDurations?: Ref<Record<string, number> | undefined> | Record<string, number>
  /** Ladder length in VISITS — after this many laps the cohort completes and
   *  leaves the sequence for good (Tom, 2026-09-19; DEFAULT_RETIRE_AFTER_VISITS
   *  = 10). Wins over whatever the per-stage durations sum to. */
  retireAfterVisits?: Ref<number | undefined> | number
  /** Fire a pod-lap every N main rounds from activation onward. Default 1
   *  (every round). Stretches every stage proportionally because the
   *  pod-round ratchet only ticks on actual fires. */
  roundInterval?: Ref<number> | number
  /**
   * THE listening play/speed policy (Tom, 2026-08-07) — one pattern, one speed
   * per phrase, exposure-ramped, clamped at 1.0. Reactive so a Supabase edit to
   * the `listening` row or the active mode's `listeningSpeedRamp` lands on the
   * next lap. Omitted ⇒ the shipped Fast policy, which is what every non-player
   * caller (tests, the admin Progression walk) wants.
   *
   * When present and `useStagePlaylist` is false — the shipped default — this
   * SUPERSEDES `stagePlaylist`/`stageDuration`/`stageDurations` entirely: there
   * are no stages any more, so those options are read only by the escape hatch.
   */
  listeningPolicy?: Ref<ListeningPlayPolicy> | ListeningPlayPolicy
  /** Course speed config (globalSpeed) — folded into the exposure ramp exactly
   *  as it was into the belt ramp. Omitted ⇒ 1.0. */
  targetSpeed?: Ref<TargetSpeedConfig> | TargetSpeedConfig
  /**
   * The LEARNER's current seed number — what the per-mode BELT CEILING is keyed
   * on (Tom, 2026-08-07 23:56Z: "a white-belt learner's ceiling is 0.8x"). A
   * pod sentence has no seed number of its own, so this is the same
   * `beltAnchorSeed` the runtime already uses for the speaking belt.
   *
   * Absent / null ⇒ the FIRST (gentlest) rung, i.e. treat an unknown learner as
   * a beginner rather than silently handing them full speed.
   */
  beltAnchorSeed?: Ref<number | null | undefined> | number | null | undefined
  /**
   * THE CLASS DOOR. While playing AS A CLASS every pod read and write below
   * belongs to the class's own learner id, and the browser can never touch it:
   * `learner_pod_state` and `course_enrollments` both carry own-row RLS, which
   * resolves to the driving STAFF member's row, never the class's. So every
   * class pod-state write was refused and only console.warned (zero rows for
   * any class, ever) and every class ratchet update was silently dropped (all
   * 199 class enrollments sat at exactly 0 — verified live 2026-09-17), which
   * restarted the pod-lap cadence and the cohort ladder from scratch every
   * single session.
   *
   * The READS go through it too. RLS HIDES rows rather than erroring, so
   * routing only the writes would have a class persist its ratchet and then
   * read back null on the next session — fixed in the database, still broken
   * in the product.
   *
   * Null/absent outside class mode, which is every other caller: own accounts
   * keep the direct client path untouched.
   */
  classRoute?: Ref<PodClassRoute | null | undefined> | PodClassRoute | null | undefined
}

/**
 * The subset of the class-aware progress store this scheduler needs
 * (`useClassProgressStore`), narrowed so tests can hand in a plain object.
 * Each method is server-mediated through /api/school/class-progress, which
 * authorises the caller against the class and resolves the class's learner id
 * itself — the client never names a learner id.
 */
export interface PodClassRoute {
  getPodRatchet?: () => Promise<{ rounds_since_pod: number | null; completed_pod_rounds: number | null } | null>
  persistPodRatchet?: (completedPodRounds: number, roundsSincePod: number) => Promise<unknown>
  resetPodRatchet?: () => Promise<unknown>
  loadPodState?: () => Promise<Array<{ sentence_id: string; exposures: number }> | null>
  upsertPodState?: (rows: Array<{ sentence_id: string; exposures: number }>) => Promise<unknown>
  deletePodState?: () => Promise<unknown>
}

/**
 * The listening pattern in POD vocabulary: 'target'/'target2' both resolve to
 * the sentence's one target clip ('ps'), 'known' to its translation ('trans').
 * A pod sentence has a single recorded target take, so the voice-2 distinction
 * Layer 1 makes is a no-op here — the pattern still emits four plays.
 *
 * The emitted ROLE is cosmetic once a uniform speed is supplied (it drives the
 * gap matrix and the display side), which is why every target slot maps to the
 * plain 'ps' role rather than a speed-bearing one like 'ps08x'.
 */
export function podPlaylistFromPattern(pattern: readonly ListeningSlot[]): PodPlayRole[] {
  return pattern.map((slot) => (slot === 'known' ? 'trans' : 'ps'))
}

const isGuestLearner = (id: string | null | undefined): boolean => {
  return !id || id === 'demo-learner' || id.startsWith('guest-')
}

const unwrap = <T,>(v: Ref<T> | T): T =>
  v && typeof v === 'object' && 'value' in (v as any) ? (v as Ref<T>).value : (v as T)

// ============================================================================
// Composable
// ============================================================================

export function usePodLapScheduler(options: UsePodLapSchedulerOptions) {
  const supabaseRef = options.supabase
  const courseCodeRef = options.courseCode
  const learnerIdRef = options.learnerId
  /** Null outside class mode — read per call, because the class context is
   *  reactive (a teacher can leave play-as-class without remounting). */
  const classRoute = (): PodClassRoute | null => unwrap(options.classRoute) ?? null

  const isInitialized = ref(false)
  const isLoading = ref(false)
  const podSentences = shallowRef<SchedulerPodRow[]>([])
  /**
   * The pod's continuations, indexed by the branch point they attach to
   * (`podSlate.branchKey(scene, sentence)`). THE CAPABILITY, NOT THE POLICY:
   * this makes a recovery servable at the coordinate it belongs to, in the
   * moment. WHEN one should fire is Tom's call and is deliberately not decided
   * here. Empty for every pod that carries no continuation, which is all of
   * them today.
   */
  const podContinuations = shallowRef<Map<string, Array<PodContinuation<RawPodRow>>>>(new Map())
  const introAudio = ref<BookendAudio | null>(null)
  const outroAudio = ref<BookendAudio | null>(null)

  /** Work debt: completed rounds since the last COMPLETED pod lap. Monotonic
   *  up, reset to 0 only by markLapCompleted. Never a function of position. */
  const roundsSincePod = ref<number>(0)
  /** Main round of the last completed round we were told about — the anchor
   *  for forward debt PROJECTION in preview callers (see firesAtRound). */
  const debtAnchorRound = ref<number>(0)
  /** Independent ratchet counter. Increments only on completed laps. */
  const completedPodRounds = ref<number>(0)

  /** Audio ids of the last plays emitted, so the A-64 cap holds across the
   *  lap boundary (laps run back to back in Listening mode). */
  let lastLapTail: string[] = []

  // ── Shared two-doors exposure counter (learner_pod_state) ────────────────
  // sentence_id → exposures COMPLETED across both doors (pod laps + Listening
  // Drill). A sentence's stage maths use max(derived alive, stored + 1) so a
  // drilled sentence enters/continues the lap ladder past the breakdown it
  // has outgrown; each completed lap writes back the served view. Guests keep
  // the derived-only behaviour (no rows).
  let podExposures = new Map<string, number>()
  /** Sentences served by the LAST built lap, with the exposure count each
   *  will have completed once that lap finishes. Flushed by markLapCompleted. */
  let pendingExposures: Array<{ sentence_id: string; exposures: number }> = []

  /**
   * Load pod sentences, bookends, and the learner's enrollment ratchet state.
   * Idempotent — safe to call again on course or learner change.
   */
  const initialize = async (): Promise<void> => {
    const supabase = unwrap(supabaseRef)
    const courseCode = unwrap(courseCodeRef)
    const learnerId = unwrap(learnerIdRef)
    if (!supabase || !courseCode) return

    const route = classRoute()
    // Bound, because the class store's methods are object-literal members.
    const podRatchetRoute = route?.getPodRatchet ? () => route.getPodRatchet!() : null

    isLoading.value = true
    try {
      // Retry before falling back to the offline snapshot — the highest-risk
      // moment for a transient failure is right after a forced sign-in
      // reload (auth/network still settling), which is exactly when a
      // silent fallback to a stale, unbounded-age snapshot serves the wrong
      // vintage of pod audio/text (2026-07-21 forum report). See
      // retryListeningRead's doc comment.
      // Which pod this course serves — resolved by servedPod, memoised per
      // course, so this is one round-trip shared with every other pod reader.
      const { podId } = await resolveServedPod(supabase, courseCode)
      const [podsResult, bookendsResult, enrollmentResult] = await retryListeningRead(
        () => Promise.all([
          supabase
            .from('listening_pod_sentences')
            .select('id, global_order, scene_number, sentence_number, speaker, target_text, known_text, target_audio_id, known_audio_id, glue_to_next, atom_map, sentence_audio_ids, sentence_known_audio_ids, variant_key, attach_sentence_number')
            .eq('pod_id', podId)
            .order('global_order', { ascending: true }),
          supabase
            .from('course_audio')
            .select('role, text, id, duration_ms')
            .eq('course_code', courseCode)
            .in('role', ['bookend_listen_intro', 'bookend_listen_outro']),
          // Guests have no enrollment row → in-memory counter only.
          // Class mode reads through the class door (see classRoute): the
          // browser's own-row RLS would return an empty result for the class's
          // row and the ratchet would restart at zero every session.
          isGuestLearner(learnerId)
            ? Promise.resolve({ data: null, error: null } as { data: null; error: null })
            : podRatchetRoute
              ? podRatchetRoute().then((data) => ({ data, error: null }))
              : supabase
                  .from('course_enrollments')
                  .select('rounds_since_pod, completed_pod_rounds')
                  .eq('learner_id', learnerId)
                  .eq('course_id', courseCode)
                  .maybeSingle(),
        ]),
        ([pods, bookends]) => !pods.error && !bookends.error,
      )

      // Offline fallback (Tom's airplane-mode test 2026-07-09): when the live
      // metadata queries fail (offline, mid-air drop), serve the rows/bookends
      // persisted by the deliberate offline download. Never-downloaded +
      // offline keeps the old failure path (init warns, pods just don't fire).
      let podRowsData = podsResult.data as RawPodRow[] | null
      let bookendData = bookendsResult.data as Array<{ role: string; text: string; id: string; duration_ms?: number }> | null
      if (podsResult.error || bookendsResult.error) {
        const cached = await getCachedListeningMeta(courseCode)
        if (cached) {
          console.warn('[podLapScheduler] live metadata fetch failed — using offline cache',
            cached.stale
              ? `(snapshot marked STALE since ${new Date(cached.stale.since).toISOString()} — refresh pending)`
              : '')
          if (podsResult.error) podRowsData = cached.podRows as unknown as RawPodRow[]
          if (bookendsResult.error) bookendData = cached.bookends
        } else {
          if (podsResult.error) throw new Error(`pod sentences: ${podsResult.error.message}`)
          throw new Error(`bookends: ${bookendsResult.error!.message}`)
        }
      }

      // A-86: stamp per-clip versioned refs (`<uuid>.v<N>`) onto the ids BEFORE
      // anything downstream turns them into `/api/audio/…` URLs or IndexedDB
      // keys. This walk is its own entry point — it does not go through
      // generateLearningScript — so without this a revised pod clip would be
      // requested at its bare uuid and served from cache forever. No-op when
      // the course has no revised clips, and empty-on-error by design: a missed
      // suffix costs one stale clip, a throw costs the whole listening lane.
      const revisedRefs = await getRevisedAudioRefs(supabase, courseCode, collectAudioIds(podRowsData || [], bookendData || []))
      podRowsData = stampRowAudioRefs(revisedRefs, (podRowsData || []) as RawPodRow[])
      bookendData = stampRowAudioRefs(revisedRefs, bookendData || [])

      // Flatten turn-rows into per-SENTENCE units: a silence-split turn plays
      // as target→known→target PER SENTENCE (not 3 target sentences then 3
      // known — too hard to follow, Tom 2026-06-16). Rows without a split pass
      // through unchanged. Everything downstream (stages, gaps) then works
      // per-sentence by construction. atom_map rides along per sentence for
      // the always-visible LEGO-tile breakdown (LearningPlayer renders it —
      // no audio ladder is built from it any more, see podTurnDisplay).
      // A pod's WALK is its base rows. A continuation (variant_key set) is
      // attached to a coordinate, not appended to the walk, so it must never
      // enter the lap sequence — a learner who never branches walks exactly
      // what they walked before this existed. It stays reachable through
      // podContinuations below, indexed by the branch point it attaches to.
      const allRows = (podRowsData || []) as RawPodRow[]
      // Use the same split-existence oracle as Listening Mode. A live pod
      // can still list slices deleted from course_audio; playing/prefetching
      // those IDs produces 404s even with a fresh bundle. Keep the canonical
      // whole-turn recording whenever either side has a missing slice.
      let splitTexts: Map<string, string> | undefined
      const splitRefs = new Map<string, string>()
      for (const row of allRows) {
        for (const id of [...(row.sentence_audio_ids || []), ...(row.sentence_known_audio_ids || [])]) {
          if (id) splitRefs.set(bareAudioId(id), id)
        }
      }
      try {
        const texts = new Map<string, string>()
        const ids = [...splitRefs.keys()]
        for (let i = 0; i < ids.length; i += 150) {
          const { data, error } = await supabase.from('course_audio').select('id,text').in('id', ids.slice(i, i + 150))
          if (error) throw error
          for (const clip of data || []) {
            const ref = splitRefs.get(clip.id)
            if (ref) texts.set(ref, clip.text || '')
          }
        }
        splitTexts = texts
      } catch (err) {
        // A failed read is not proof that the clips are missing. The offline
        // snapshot carries the same oracle; keep the existing offline path
        // if no such snapshot is available.
        console.warn('[podLapScheduler] split-clip lookup failed; using cached texts', err)
        const cached = await getCachedListeningMeta(courseCode)
        if (cached?.clipTexts) splitTexts = new Map(Object.entries(cached.clipTexts))
      }
      podContinuations.value = continuationsByBranch(allRows as unknown as SlateRow[]) as unknown as Map<string, Array<PodContinuation<RawPodRow>>>
      podSentences.value = flattenPodRows(baseSlate(allRows), splitTexts)

      const byRole = new Map<string, BookendAudio>()
      for (const row of bookendData || []) {
        byRole.set(row.role, { id: row.id, text: row.text, duration_ms: row.duration_ms })
      }
      introAudio.value = byRole.get('bookend_listen_intro') || null
      outroAudio.value = byRole.get('bookend_listen_outro') || null

      // Shared exposure counters — best-effort: a failed read degrades to the
      // derived-only behaviour (identical to pre-bridge), never blocks init.
      podExposures = new Map()
      pendingExposures = []
      if (!isGuestLearner(learnerId)) {
        try {
          if (route?.loadPodState) {
            podExposures = new Map(
              (await route.loadPodState() ?? []).map((r) => [r.sentence_id, r.exposures] as const),
            )
          } else {
            podExposures = await new PodStateStore({ client: supabase }).loadAll(learnerId!, courseCode)
          }
        } catch (err) {
          console.warn('[podLapScheduler] pod state read failed (derived-only):', err)
        }
      }

      const enrollment = (enrollmentResult as any)?.data
      if (enrollment) {
        roundsSincePod.value = Math.max(0, enrollment.rounds_since_pod ?? 0)
        completedPodRounds.value = enrollment.completed_pod_rounds ?? 0
      } else {
        // Brand-new enrollment, guest, or read failed — start the debt at 0
        // and let this session's own work earn the first lap.
        roundsSincePod.value = 0
        completedPodRounds.value = 0
      }

      isInitialized.value = true
    } catch (err) {
      console.warn('[podLapScheduler] init failed:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Sticky flag that forces the NEXT round to fire a lap regardless of the
   * cadence rule. Set by deferLap() — typically when a session resumes
   * mid-round and the remaining cycles are too few for the pod audio to
   * pre-warm. Cleared once we actually consume the lap (markLapCompleted).
   * Cadence anchor is unaffected, so the regular schedule
   * resumes normally on the round after the deferred firing.
   */
  const deferredPodPending = ref(false)

  const podInterval = (): number =>
    Math.max(1, Math.floor(unwrap(options.roundInterval) ?? 1))

  /**
   * Count one COMPLETED round of work against the debt. Called from the
   * player's round-completed handler for EVERY round — replays, easy mode,
   * revival tail, listening rounds — because the debt measures work done, not
   * position reached. `mainRound` is only the projection anchor for preview
   * callers; the fire decision never reads it. Persist is fire-and-forget:
   * a failed write costs at most one round of debt, never playback.
   */
  const noteRoundCompleted = (mainRound?: number): void => {
    roundsSincePod.value += 1
    if (typeof mainRound === 'number' && Number.isFinite(mainRound)) {
      debtAnchorRound.value = mainRound
    }
    void persistRatchet()
  }

  /**
   * THE FIRE PREDICATE. A lap is due when the work debt has reached the
   * interval and a lap can compose. No position term, by design.
   */
  const isLapDue = (): boolean => {
    if (!isInitialized.value) return false
    if (podSentences.value.length === 0) return false
    // Every cohort has completed the ladder (Tom 2026-09-06) — the pod track is
    // finished for this course, so it stops claiming boundaries entirely rather
    // than claiming one every five rounds and composing nothing.
    if (allCohortsCompleted()) return false
    if (deferredPodPending.value) return true
    return roundsSincePod.value >= podInterval()
  }

  /**
   * PREVIEW ONLY — "would a lap fall at main round R, if the learner walks
   * forward from here completing one round per round?" Debt is not a pure
   * function of round number, so this projects the CURRENT debt forward from
   * the anchor with no resets other than its own fires. Used by span audio
   * pre-warm and the sector-merge cursor, both of which ask "does a lap fall
   * within this span" rather than "exactly which round". The live fire
   * decision uses isLapDue(), never this.
   */
  const shouldFireLapAt = (mainRound: number): boolean => {
    if (!isInitialized.value) return false
    if (podSentences.value.length === 0) return false
    if (allCohortsCompleted()) return false
    if (deferredPodPending.value) return true
    const interval = podInterval()
    const projected = roundsSincePod.value + (mainRound - debtAnchorRound.value)
    if (projected < interval) return false
    return (projected - interval) % interval === 0
  }

  /**
   * Defer the next due lap by one round. Caller invokes this when a pod is
   * about to fire but there aren't enough cycles left to pre-warm its audio
   * (resume mid-round). The defer flag forces shouldFireLapAt to return true
   * on the NEXT round entry, giving a full round of runway for prefetchLap
   * to land the audio.
   */
  const deferLap = (): void => {
    deferredPodPending.value = true
  }

  /**
   * Fire-and-forget warm-up of the next lap's audio. Pods live behind a
   * full round (~5 min) of buffer when prefetched on round entry.
   *
   * If `ensureFn` is provided, audio is landed in IndexedDB via that
   * callback (typically `audioCache.persistent.ensure`). The pod playback
   * path resolves URLs through AudioCacheSource at play time, so once
   * the bytes are in IndexedDB, playback uses blob URLs — no SW
   * round-trip per play. Aligns with the "asset + program" architecture
   * for main-flow pod laps where stage-driven gapless playback matters.
   *
   * Fallback (no ensureFn): warm the SW CacheFirst layer via plain
   * fetch with priority='low'. Still cached, but goes through SW on
   * playback.
   *
   * No-op if there's no lap to fetch. Idempotent — repeat calls are
   * dedup'd by the underlying cache layer.
   */
  const prefetchLap = (ensureFn?: (audioId: string) => Promise<void>): void => {
    if (podSentences.value.length === 0) return
    const lap = nextLap()
    if (!lap) return
    const ids = new Set<string>()
    if (lap.intro?.id) ids.add(lap.intro.id)
    if (lap.outro?.id) ids.add(lap.outro.id)
    for (const p of lap.plays) {
      if (p.audioId) ids.add(p.audioId)
    }
    if (ensureFn) {
      for (const id of ids) {
        ensureFn(id).catch(() => undefined)
      }
    } else {
      for (const id of ids) {
        const url = apiUrl(`/api/audio/${id}`)
        fetch(url, { priority: 'low' }).catch(() => undefined)
      }
    }
  }

  // Main-stage composition lives in ./podStageComposition (buildMainStage).

  // ── Cohort intake (Tom 2026-07-24: scene walls, exchange debuts) ──────────
  // Each lap introduces a COHORT — one EXCHANGE (a speaker turn plus its
  // reply, turns = glue_to_next runs), never crossing a scene boundary: a new
  // scene debuts by its opening exchange and each later lap extends the SAME
  // scene by its next exchange until it completes (supersedes the 2026-07-23
  // whole-scene-per-lap model — scenes 6+ run 27-36 sentences, ~110+ plays in
  // one debut lap). Every sentence introduced together stays at the SAME
  // stage forever — alive counting is per cohort, not per sentence index. The
  // partition is pure structure over podSentences (@ssi/core/pods
  // podCohorts.ts); memoised on the array identity so it recomputes only when
  // a course (re)load swaps the sentence list.
  let cohortsCache: { rows: SchedulerPodRow[]; cohorts: PodCohort[] } | null = null
  const getCohorts = (): PodCohort[] => {
    const rows = podSentences.value
    if (!cohortsCache || cohortsCache.rows !== rows) {
      cohortsCache = { rows, cohorts: buildPodCohorts(rows) }
    }
    return cohortsCache.cohorts
  }

  // Snapshot the live stage config — stagePlaylist keys are strings in JSON,
  // numbers in the default; normalise via `String(stage)` lookup. totalStages
  // is derived from the actual key count so admins can add or remove stages
  // from the admin page without code changes. Shared by nextLap and the
  // preview-cheat fallback below.
  const resolveStageConfig = () => {
    const livePlaylist = unwrap(options.stagePlaylist) as Record<string | number, PodPlayRole[]> | undefined
    const liveDuration = unwrap(options.stageDuration) as number | undefined
    const liveDurations = unwrap(options.stageDurations) as Record<string, number> | undefined
    const liveRetire = unwrap(options.retireAfterVisits) as number | undefined
    const stagePlaylistMap: Record<string | number, PodPlayRole[]> = livePlaylist || DEFAULT_STAGE_PLAYLIST
    const stageDuration: number = liveDuration ?? DEFAULT_STAGE_DURATION
    // Pair per-stage durations with their playlist: a live (admin-saved)
    // playlist without stageDurations keeps uniform legacy maths; the code
    // defaults pair DEFAULT_STAGE_PLAYLIST with DEFAULT_STAGE_DURATIONS.
    const stageDurationsMap: Record<string | number, number> | undefined =
      liveDurations ?? (livePlaylist ? undefined : DEFAULT_STAGE_DURATIONS)
    const totalStages = Object.keys(stagePlaylistMap).length
    // Ten visits and then gone. A live row that predates the knob keeps the
    // shipped 10 rather than the old derived-from-durations length, which is
    // what grew laps without bound; pass 0 or a negative to opt back out.
    const retireAfterVisits: number | undefined =
      typeof liveRetire === 'number' ? liveRetire : DEFAULT_RETIRE_AFTER_VISITS
    return { stagePlaylistMap, stageDuration, stageDurationsMap, totalStages, retireAfterVisits }
  }

  /**
   * Has EVERY cohort in this course completed the ladder (Tom, 2026-09-06)?
   * Then the pod track is done: it stops claiming listening boundaries, so the
   * learner falls cleanly through to the Layer-1 seed cup instead of hitting a
   * boundary that composes nothing every five rounds. The pods themselves stay
   * in the Pods tab for ever.
   *
   * Cheap: cohorts are memoised, and only the YOUNGEST cohort has to be tested
   * — derived alive falls as the cohort gets newer, and the drill lift only
   * ever raises a cohort's alive, so if the youngest has completed, all have.
   * Intake unfinished (cohorts still waiting to debut) ⇒ never "all done".
   */
  const allCohortsCompleted = (): boolean => {
    const cohorts = getCohorts()
    if (cohorts.length === 0) return false
    const podRound = podCohortRoundFor(cohorts, completedPodRounds.value)
    if (podRound < cohorts.length) return false
    const { stageDuration, stageDurationsMap, totalStages, retireAfterVisits } = resolveStageConfig()
    return podCohortHasCompleted(podRound - cohorts.length + 1, stageDuration, totalStages, stageDurationsMap, retireAfterVisits)
  }

  /**
   * The live listening policy (Tom's 2026-08-07 one-mode redesign). Absent ⇒
   * the shipped FAST policy — one T·K·T·T pattern, a flat 1.0 ramp, ceiling
   * 1.0 — so a caller that passes nothing (tests, previews) still gets the
   * simplified behaviour rather than the retired stage ladder.
   */
  const resolveListeningPolicy = (): ListeningPlayPolicy =>
    (unwrap(options.listeningPolicy) as ListeningPlayPolicy | undefined) ?? {
      pattern: [...DEFAULT_LISTENING_PATTERN],
      ramp: [...DEFAULT_FAST_LISTENING_RAMP],
      beltCeilings: [...DEFAULT_FAST_BELT_CEILINGS],
      ceiling: LISTENING_SPEED_CEILING,
      speedSource: 'exposure',
      useStagePlaylist: false,
    }

  const courseGlobalSpeed = (): number => {
    const cfg = unwrap(options.targetSpeed) as TargetSpeedConfig | undefined
    const g = cfg?.globalSpeed
    return typeof g === 'number' && Number.isFinite(g) && g > 0 ? g : 1.0
  }

  /**
   * Compose the lap that should play right now, based on the current ratchet
   * value. Returns null if there's nothing to play (no sentences, or every
   * sentence's audio is missing).
   */
  const nextLap = (): PodLap | null => {
    if (podSentences.value.length === 0) return null

    const cohorts = getCohorts()
    if (cohorts.length === 0) return null
    // `completedPodRounds` stores SENTENCES covered (its historical unit —
    // one legacy lap introduced one sentence). The cohort-round derives from
    // it: round N introduces cohort N and is the stage-aging clock. Legacy
    // mid-cohort values count their started cohort; values past the end keep
    // aging +1 per lap (see podCohortRoundFor).
    const podRound = podCohortRoundFor(cohorts, completedPodRounds.value)
    const activeCohorts = Math.min(podRound, cohorts.length)

    const { stagePlaylistMap, stageDuration, stageDurationsMap, totalStages, retireAfterVisits } = resolveStageConfig()

    // ONE MODE (Tom, 2026-08-07). The nine-stage ladder is retired: unless the
    // escape hatch is set, every cohort at every age plays the SAME four-slot
    // pattern, and the only thing its exposure count decides is the single
    // speed all four slots share. `stageInfo` is still computed because its
    // `alive` count IS the exposure count, and the emitted `stage` field is
    // still stamped on each play for the Progression badge and telemetry.
    const policy = resolveListeningPolicy()
    const globalSpeed = courseGlobalSpeed()
    // The BELT CEILING is the learner's, not the sentence's — one value for the
    // whole lap. The exposure ramp then approaches it from below, per cohort.
    const beltCeiling = beltCeilingForSeed(
      unwrap(options.beltAnchorSeed) as number | null | undefined,
      policy.beltCeilings,
    )
    const singlePlaylist = podPlaylistFromPattern(policy.pattern)

    const plays: PodPlay[] = []
    pendingExposures = []
    for (let c = 1; c <= activeCohorts; c++) {
      const cohort = cohorts[c - 1]
      const members = podSentences.value.slice(cohort.start, cohort.start + cohort.size)

      // STAGE COHESION (Tom 2026-07-23): the cohort moves through the DK
      // sequence as ONE unit — every member serves the same alive count.
      // Derived position is the cohort's lap-ladder age; the shared two-doors
      // counter (Listening Drill) can still lift the WHOLE cohort, but only
      // as far as its least-drilled member (min), so drilling one line never
      // fast-forwards its cohort-mates past their early stages. Intake stays
      // ratchet-driven — drilling ahead never pulls a cohort into laps early.
      //
      // Every cohort enters straight at Stage 1 on its first view (Stage-0
      // retired 2026-07-14). Its intended replacement — the always-visible
      // LEGO-tile display PodTurnDisplay built from atom_map — was itself
      // replaced on 2026-07-22 by the karaoke teleprompter, so there is
      // currently NO per-atom breakdown of a pod sentence in the main flow,
      // audio or visual. Stated here because this comment used to promise one.
      const derivedAlive = podRound - c + 1
      const storedLift = Math.min(...members.map((m) =>
        (m.sentence_id ? (podExposures.get(m.sentence_id) ?? 0) : 0) + 1))
      const alive = Math.max(derivedAlive, storedLift)

      // COMPLETES AND DISAPPEARS (Tom, 2026-09-06). A cohort that has finished
      // the ladder leaves the sequence for good — it is not emitted into this
      // lap or any later one. It stays playable for ever in the Pods tab of
      // Listening mode. Applies on BOTH paths (one-mode and the retired
      // stage-playlist escape hatch): the ruling is about the sequence, not
      // about the pattern.
      //
      // Judged on the DERIVED age only — the laps the main flow has actually
      // served this cohort — never on the Drill lift. The lift may raise the
      // rung a cohort is served at, as far as the top one, but a Listening-mode
      // drill is not a lap of the sequence and cannot take a cohort out of it.
      // This is also what allCohortsCompleted() already assumes; the two were
      // out of step. Job #350 (2026-09-12): Tom's zho_for_eng pod-state rows
      // carried exposures of 96..461 on every sentence against a ratchet of 45,
      // so with `alive` here every cohort completed on intake and the main flow
      // composed no pod dialogue at all.
      if (podCohortHasCompleted(derivedAlive, stageDuration, totalStages, stageDurationsMap, retireAfterVisits)) continue

      const stageInfo = podStageFor(1, alive, stageDuration, totalStages, stageDurationsMap)
      if (!stageInfo) continue
      // Escape hatch only: replay the retired ladder. Default path takes the
      // single pattern and ignores the stage playlist entirely, which is what
      // makes the simplification hold even when the live `pods` DB row still
      // carries all nine stages (it does — a live row overrides code defaults).
      const playlist = policy.useStagePlaylist
        ? (stagePlaylistMap[stageInfo.stage] || stagePlaylistMap[String(stageInfo.stage)])
        : singlePlaylist
      if (!playlist) continue
      // `alive` is the cohort's exposure count — the number of laps this
      // sentence has been in play. `undefined` restores the historic per-role
      // ROLE_SPEED rates, which is what the stage-playlist path and
      // speedSource:'belt' both want (the belt curve is keyed on a seed
      // number, which Layer-2 pod sentences do not have — pods never rode it,
      // so 'belt' means "as pods were").
      //
      // THE 1.0 CEILING IS RETIRED FOR PODS, DELIBERATELY (Tom, 2026-08-24).
      // LISTENING_SPEED_CEILING (1.0) lives inside resolveListeningSpeed, and
      // the stage-playlist branch below does not call it — so the fade's
      // closing ps2x reps play at the 2.0× they are authored at. That used to
      // be an accident of `undefined`; it is now the ruling. A pod has its own
      // completion signal — reaching the final bare-target-at-2× stage IS the
      // signal, and since 2026-09-06 it literally completes there — so it needs
      // no speed ceiling on top. The ceiling itself
      // is UNCHANGED and still absolute for every non-pod listening path: the
      // `else` arm here (Layer-1 seed sandwiches, speedSource:'exposure')
      // still runs through resolveListeningSpeed and is still clamped to 1.0.
      // Locked by podFadeSchedule.test.ts.
      const uniformSpeed = policy.useStagePlaylist || policy.speedSource !== 'exposure'
        ? undefined
        : resolveListeningSpeed(alive, policy.ramp, globalSpeed, policy.ceiling, beltCeiling)
      for (let k = 0; k < members.length; k++) {
        const sentence = members[k]
        if (!sentence.target_audio_id) continue
        // Whole-sentence composition (end-on-target
        // invariant, glue) is the shared builder.
        plays.push(...buildMainStage(sentence, stageInfo.stage, cohort.start + k + 1, playlist, uniformSpeed))
        if (sentence.sentence_id) pendingExposures.push({ sentence_id: sentence.sentence_id, exposures: alive })
      }
    }
    if (plays.length === 0) return null

    const hasBookends = !!(introAudio.value && outroAudio.value)

    // A-64 floor. Applied here, downstream of the admin-editable stage
    // playlists in algorithm_config, so no saved playlist can breach the law.
    // The seed carries the previous lap's tail across the boundary — laps run
    // back to back in Listening mode, so a one-sentence pod ending ['ps2x',
    // 'ps2x'] and restarting on the same clip is a real three-in-a-row. When
    // bookends play between laps they genuinely separate the two, so the seed
    // is not carried. minKeep: 1 means a degenerate single-clip lap is never
    // emptied — the session stays alive and the shortfall is logged.
    const capped = capConsecutiveRepeats(plays, podPlayIdentity, {
      seed: hasBookends ? [] : lastLapTail,
      minKeep: 1,
    })
    lastLapTail = capped.tail
    if (capped.dropped.length > 0 || capped.forcedKeeps > 0) {
      console.debug(
        `[usePodLapScheduler] A-64 cap: lap ${podRound} dropped ${capped.dropped.length} surplus consecutive play(s)` +
        `${capped.forcedKeeps > 0 ? `, ${capped.forcedKeeps} kept to avoid an empty lap` : ''}` +
        ` (pool too small to re-interleave)`,
      )
    }

    return {
      podRound,
      intro: hasBookends ? introAudio.value : null,
      plays: capped.items,
      outro: hasBookends ? outroAudio.value : null,
    }
  }

  /**
   * ?pod=1 preview-cheat fallback ONLY — never called from the real cadence
   * path. nextLap() only ever composes from the ratchet-windowed slice
   * [0, completedPodRounds] of podSentences; for an account whose ratchet
   * hasn't advanced past a run of sentences that fail to compose (missing
   * audio, empty buildMainStage output), that whole window can be
   * unplayable even though the course has good pod content elsewhere. This
   * ignores the ratchet window entirely and scans the FULL sentence list,
   * nearest-to-the-ratchet-cursor first, for the first sentence that
   * composes a playable Stage 1 (debut) lap — so the preview can always
   * demonstrate the format when the course has ANY pod content, independent
   * of the learner's ratchet position. Returns null only when the course's
   * pod content is empty or entirely unplayable.
   *
   * `startIndex` (0-based into podSentences) overrides the ratchet cursor —
   * the ?podview=1 instant preview's Next/Prev nav uses this to compose the
   * lap for a specific COHORT (the one containing that sentence), still
   * falling back outward cohort-by-cohort if it doesn't compose (missing
   * audio, empty buildMainStage). Previewing whole cohorts makes the preview
   * feel like actual dialogue — the same exchange a real lap would debut.
   */
  const nextLapPreviewFallback = (startIndex?: number): PodLap | null => {
    const TOTAL = podSentences.value.length
    if (TOTAL === 0) return null
    const cohorts = getCohorts()
    if (cohorts.length === 0) return null
    const policy = resolveListeningPolicy()
    const { stagePlaylistMap } = resolveStageConfig()
    // Same one-mode rule as nextLap: the preview must demonstrate what the
    // learner actually hears, so it takes the single pattern at first-exposure
    // speed unless the escape hatch is on.
    const playlist = policy.useStagePlaylist
      ? (stagePlaylistMap[1] || stagePlaylistMap['1'])
      : podPlaylistFromPattern(policy.pattern)
    if (!playlist) return null
    const previewSpeed = policy.useStagePlaylist || policy.speedSource !== 'exposure'
      ? undefined
      : resolveListeningSpeed(
          1,
          policy.ramp,
          courseGlobalSpeed(),
          policy.ceiling,
          beltCeilingForSeed(unwrap(options.beltAnchorSeed) as number | null | undefined, policy.beltCeilings),
        )

    // Search outward from the cohort under the cursor (nearest to "current
    // position") so the preview shows content close to where the learner
    // actually is, falling back to the whole course only if that immediate
    // neighbourhood has nothing playable.
    const cursorSentence = Math.max(0, Math.min(startIndex ?? completedPodRounds.value, TOTAL - 1))
    const cursor = Math.max(0, podCohortOrdinalForIndex(cohorts, cursorSentence))
    const order: number[] = [cursor]
    for (let d = 1; d < cohorts.length; d++) {
      if (cursor - d >= 0) order.push(cursor - d)
      if (cursor + d < cohorts.length) order.push(cursor + d)
    }

    for (const ci of order) {
      const cohort = cohorts[ci]
      const plays: PodPlay[] = []
      for (let k = 0; k < cohort.size; k++) {
        const sentence = podSentences.value[cohort.start + k]
        if (!sentence.target_audio_id) continue
        plays.push(...buildMainStage(sentence, 1, cohort.start + k + 1, playlist, previewSpeed))
      }
      if (plays.length === 0) continue
      const hasBookends = !!(introAudio.value && outroAudio.value)
      return {
        podRound: podCohortRoundFor(cohorts, completedPodRounds.value),
        intro: hasBookends ? introAudio.value : null,
        plays,
        outro: hasBookends ? outroAudio.value : null,
      }
    }
    return null
  }

  /**
   * ?podview=1 nav helper: the start sentence-index of the cohort adjacent
   * (dir = ±1) to the one containing `index`, wrapping at the ends — so
   * Next/Prev step whole exchanges, matching what each preview lap plays.
   */
  const previewCohortStep = (index: number, dir: 1 | -1): number => {
    const cohorts = getCohorts()
    if (cohorts.length === 0) return 0
    const clamped = Math.max(0, Math.min(index, podSentences.value.length - 1))
    const cur = Math.max(0, podCohortOrdinalForIndex(cohorts, clamped))
    const next = (cur + dir + cohorts.length) % cohorts.length
    return cohorts[next].start
  }

  /**
   * Advance the ratchet past the cohort this lap introduced. Call this only
   * after the user has played the lap to completion — skipped laps must NOT
   * call this. The stored unit stays SENTENCES covered (so legacy values and
   * old clients interoperate on the same enrollment row); completing a lap
   * snaps to the introduced cohort's end boundary, or ticks +1 once every
   * cohort is in (eternal aging).
   */
  const markLapCompleted = async (): Promise<void> => {
    completedPodRounds.value = podRatchetAfterLap(getCohorts(), completedPodRounds.value)
    // The debt is PAID — and only completion pays it. Skips, failures,
    // offline-incomplete laps and composed-nothing boundaries all leave it
    // standing so the next viable boundary fires.
    roundsSincePod.value = 0
    debtAnchorRound.value = 0
    deferredPodPending.value = false
    await persistRatchet()
    await flushExposures()
  }

  /**
   * Write the last-built lap's exposure counts to the shared two-doors
   * counter (learner_pod_state). In-memory map merges by max first so the
   * next lap build sees the advance even if the network write fails; guests
   * keep derived-only behaviour. Best-effort — never blocks playback.
   */
  const flushExposures = async (): Promise<void> => {
    const batch = pendingExposures
    pendingExposures = []
    if (batch.length === 0) return
    for (const r of batch) {
      if ((podExposures.get(r.sentence_id) ?? 0) < r.exposures) podExposures.set(r.sentence_id, r.exposures)
    }
    const supabase = unwrap(supabaseRef)
    const learnerId = unwrap(learnerIdRef)
    const courseCode = unwrap(courseCodeRef)
    if (!supabase || !courseCode || !learnerId || isGuestLearner(learnerId)) return
    // The map holds the max seen this session — write that, so a stale batch
    // never regresses a counter another pass just advanced.
    const rows = batch.map((r) => ({
      sentence_id: r.sentence_id,
      exposures: podExposures.get(r.sentence_id) ?? r.exposures,
    }))
    try {
      const route = classRoute()
      if (route?.upsertPodState) {
        await route.upsertPodState(rows)
        return
      }
      await new PodStateStore({ client: supabase }).upsertMany(
        rows.map((r) => ({ learner_id: learnerId, course_code: courseCode, ...r })),
      )
    } catch (err) {
      console.warn('[podLapScheduler] pod state write failed:', err)
    }
  }

  /**
   * Reset the ratchet and the work debt (for course reset).
   */
  const reset = async (): Promise<void> => {
    completedPodRounds.value = 0
    roundsSincePod.value = 0
    debtAnchorRound.value = 0
    podExposures = new Map()
    pendingExposures = []

    const supabase = unwrap(supabaseRef)
    const learnerId = unwrap(learnerIdRef)
    const courseCode = unwrap(courseCodeRef)
    if (!supabase || !courseCode || !learnerId || isGuestLearner(learnerId)) return
    try {
      const route = classRoute()
      if (route?.resetPodRatchet) {
        await route.resetPodRatchet()
      } else {
        await supabase
          .from('course_enrollments')
          .update({ completed_pod_rounds: 0, rounds_since_pod: 0 })
          .eq('learner_id', learnerId)
          .eq('course_id', courseCode)
      }
      // Course reset clears the shared two-doors counter too.
      if (route?.deletePodState) {
        await route.deletePodState()
      } else {
        await new PodStateStore({ client: supabase }).deleteAll(learnerId, courseCode)
      }
    } catch (err) {
      console.warn('[podLapScheduler] reset write failed:', err)
    }
  }

  /**
   * Persist the ratchet counter to course_enrollments. Guests no-op.
   * Errors are logged but don't throw — runtime audio shouldn't depend on
   * a successful DB write.
   */
  const persistRatchet = async (): Promise<void> => {
    const supabase = unwrap(supabaseRef)
    const learnerId = unwrap(learnerIdRef)
    const courseCode = unwrap(courseCodeRef)
    if (!supabase || !courseCode || !learnerId || isGuestLearner(learnerId)) return
    try {
      const route = classRoute()
      if (route?.persistPodRatchet) {
        await route.persistPodRatchet(completedPodRounds.value, roundsSincePod.value)
        return
      }
      const { error } = await supabase
        .from('course_enrollments')
        .update({
          completed_pod_rounds: completedPodRounds.value,
          rounds_since_pod: roundsSincePod.value,
        })
        .eq('learner_id', learnerId)
        .eq('course_id', courseCode)
      if (error) console.warn('[podLapScheduler] persist error:', error.message)
    } catch (err) {
      console.warn('[podLapScheduler] persist threw:', err)
    }
  }

  return {
    // State
    isInitialized,
    isLoading,
    roundsSincePod,
    completedPodRounds,
    podSentences,
    podContinuations,
    introAudio,
    outroAudio,

    // Methods
    initialize,
    isLapDue,
    noteRoundCompleted,
    shouldFireLapAt,
    nextLap,
    nextLapPreviewFallback,
    previewCohortStep,
    prefetchLap,
    deferLap,
    markLapCompleted,
    reset,
  }
}
